// Reusable, workspace-agnostic Case Export dialog: field selection -> confirmation -> download.
import { useState, useEffect } from 'react';
import portalApi from '../lib/api';
import { Loader2, Download, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

export function CaseExportDialog({ open, onClose, workspaceId, filters, exportCount, source, selectedIds }) {
  const [groups, setGroups] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('fields'); // 'fields' | 'confirm'
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('fields'); setChosen(new Set());
    setLoading(true);
    portalApi.getCaseExportFields(workspaceId)
      .then((d) => setGroups(d.groups || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [open, workspaceId]);

  const allKeys = groups.flatMap((g) => g.fields.map((f) => f.key));
  const labelFor = (k) => { for (const g of groups) { const f = g.fields.find((x) => x.key === k); if (f) return f.label; } return k; };

  const toggle = (k, on) => setChosen((prev) => { const n = new Set(prev); on ? n.add(k) : n.delete(k); return n; });
  const selectAll = () => setChosen(new Set(allKeys));
  const clearAll = () => setChosen(new Set());

  const chosenList = allKeys.filter((k) => chosen.has(k));

  const doExport = async () => {
    setExporting(true);
    try {
      const payload = { workspace_id: workspaceId, fields: chosenList, ...filters };
      if (selectedIds && selectedIds.length) payload.selected_ids = selectedIds;
      const blob = await portalApi.exportCasesCustom(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'cases_export.csv'; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportCount} case${exportCount === 1 ? '' : 's'}`);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setExporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="case-export-dialog">
        <DialogHeader>
          <DialogTitle>Export Cases</DialogTitle>
          <DialogDescription>{source} · {exportCount} case{exportCount === 1 ? '' : 's'}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : step === 'fields' ? (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{chosen.size} field{chosen.size === 1 ? '' : 's'} selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="export-select-all-fields">Select All Fields</Button>
                <Button variant="outline" size="sm" onClick={clearAll} data-testid="export-clear-fields">Clear All</Button>
              </div>
            </div>
            <div className="space-y-4 mt-2">
              {groups.map((g) => (
                <div key={g.group} data-testid={`export-group-${g.group}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{g.group}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {g.fields.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={chosen.has(f.key)} onCheckedChange={(v) => toggle(f.key, !!v)} data-testid={`export-field-${f.key}`} />
                        <span className="text-slate-700">{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {groups.length === 0 && <p className="text-sm text-slate-400">No exportable fields available.</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => setStep('confirm')} disabled={chosen.size === 0} data-testid="export-continue-btn">Continue</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-slate-200 p-4 space-y-3 mt-3" data-testid="export-summary">
              <div className="flex items-center gap-2 text-slate-900 font-semibold"><FileText className="h-4 w-4 text-sky-500" /> Export Summary</div>
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-slate-500">Cases to export</span><span className="text-slate-900 font-medium" data-testid="summary-count">{exportCount}</span>
                <span className="text-slate-500">Source</span><span className="text-slate-900">{source}</span>
                <span className="text-slate-500">Selected fields</span><span className="text-slate-900">{chosenList.length}</span>
                <span className="text-slate-500">File format</span><span className="text-slate-900">CSV</span>
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                {chosenList.map((k) => <span key={k} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{labelFor(k)}</span>)}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('fields')}>Back</Button>
              <Button onClick={doExport} disabled={exporting} data-testid="export-confirm-btn">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Export {exportCount} Case{exportCount === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CaseExportDialog;
